import { Button, Typography } from 'omakase';
import { StoreType } from '@tock/shared/utils/connect';
import { consumer, core } from '@tock/coretypes';
import { currencyCodeToSymbol } from '@tock/money';
import { getLoyaltyProgramDetails } from 'components/loyalty-program/LoyaltyProgramConstants';
import { pluralize } from '@tock/shared/utils/pluralize';
import Anchor from 'components/Anchor';
import BaseComponent from '@tock/shared/components/BaseComponent';

interface Props {
  business: consumer.IBusinessConfig;
  loyaltyProgram: StoreType['loyaltyProgram'];
  onEditLogin(): void;
  onEditRedemption(): void;
  onShowLogin(): void;
  purchaseOrder?: core.PurchaseOrder;
  ticketType?: core.ITicketType;
}

export default class LoyaltyProgram extends BaseComponent<Props> {
  render() {
    const {
      business,
      loyaltyProgram,
      onEditLogin,
      onEditRedemption,
      onShowLogin,
      purchaseOrder,
      ticketType,
    } = this.props;

    const loyaltyProgramDetails = getLoyaltyProgramDetails(
      business?.loyaltyProgramTheme ?? undefined
    );

    const balance = loyaltyProgram.balance;
    const isConnectedToLoyaltyProgram = loyaltyProgram?.balance?.username ?? '';
    const numPointsToRedeem = loyaltyProgram?.numPointsToRedeem ?? 0;
    const possiblePointsToRedeem = loyaltyProgram?.balance?.numPointsToRedeem ?? 0;
    const hasPositiveSubtotal = (purchaseOrder?.subtotal ?? 0) > 0;
    const canRedeemPoints =
      !(ticketType?.disableLoyaltyProgramRedemption ?? true) && hasPositiveSubtotal;
    const canAccruePoints =
      !(ticketType?.disableLoyaltyProgramAccrual ?? true) && hasPositiveSubtotal;
    const disableLogin = loyaltyProgramDetails?.disableLogin ?? false;
    const renderDefaultLearnMoreElement = () => (
      <>
        Not a member?{' '}
        <Anchor
          className="ConsumerCheckout-loyaltyProgram--join"
          href={loyaltyProgramDetails.learnMoreUrl}
          target="_blank"
        >
          Learn how to join the {loyaltyProgramDetails.clubName}.
        </Anchor>
      </>
    );

    return (
      <div className="ConsumerCheckout-loyaltyProgram">
        {!isConnectedToLoyaltyProgram && (
          <div>
            <h4>
              <Typography color="inherit" component="span" variant="body02Strong">
                {loyaltyProgramDetails.programName}
              </Typography>
            </h4>
            <p className="ConsumerCheckout-loyaltyProgram--loginText">
              <Typography color="inherit" component="span" variant="body02">
                {canRedeemPoints
                  ? loyaltyProgramDetails.connectText
                  : canAccruePoints
                    ? `Earn ${loyaltyProgramDetails.pointsName} from ${loyaltyProgramDetails.businessName} with this purchase.`
                    : `Connect with the ${loyaltyProgramDetails.programName}.`}{' '}
                {loyaltyProgramDetails.learnMoreElement ?? renderDefaultLearnMoreElement()}
              </Typography>
            </p>
            {!disableLogin && (
              <div className="ConsumerCheckout-loyaltyProgram--loginButton">
                <Typography color="inherit" component="span" variant="body02">
                  <Button variant="basic" size="small" onClick={onShowLogin}>
                    Log in to start
                  </Button>
                </Typography>
              </div>
            )}
          </div>
        )}
        {isConnectedToLoyaltyProgram && (
          <>
            <div className="ConsumerCheckout-loyaltyProgram--header">
              <Typography color="inherit" component="span" variant="body02Strong">
                {loyaltyProgramDetails.programName}
              </Typography>
            </div>
            <div>
              <div className="ConsumerCheckout-loyaltyProgram--accountSection ConsumerCheckout-section--gray">
                {loyaltyProgramDetails.logoUrl && (
                  <img
                    alt="Loyalty program"
                    className="ConsumerCheckout-loyaltyProgram-smallLogo"
                    src={loyaltyProgramDetails.logoUrl}
                  />
                )}
                <div className="ConsumerCheckout-loyaltyProgram--enrollmentDetails">
                  {!disableLogin && !!balance && (
                    <div>
                      <Typography color="inherit" component="span" variant="body02Strong">
                        {balance.username} ({balance.maskedCardNumber})
                      </Typography>
                      <Typography color="inherit" component="span" variant="body02">
                        <Anchor
                          className="ConsumerCheckout-loyaltyProgram--actionLink"
                          onClick={onEditLogin}
                        >
                          Unlink account
                        </Anchor>
                        <div className="ConsumerCheckout-loyaltyProgram--membershipLevel">
                          {balance.membershipLevel}
                        </div>
                      </Typography>
                    </div>
                  )}
                  {disableLogin && !!balance && (
                    <Typography color="inherit" component="span" variant="body02Strong">
                      Using account {balance.username}
                    </Typography>
                  )}
                </div>
              </div>
              {canAccruePoints && (
                <div className="ConsumerCheckout-loyaltyProgram--syncInfo">
                  <Typography color="inherit" component="span" variant="body02">
                    You will receive your {loyaltyProgramDetails.pointsName} within 24 hours after
                    your reservation.
                  </Typography>
                </div>
              )}

              {canRedeemPoints && possiblePointsToRedeem > 0 && (
                <div className="ConsumerCheckout-loyaltyProgram--pointSection">
                  <div className="ConsumerCheckout-loyaltyProgram--enrollmentDetails">
                    <Typography color="inherit" component="span" variant="body02">
                      {numPointsToRedeem > 0 && (
                        <>
                          Using{' '}
                          {currencyCodeToSymbol(
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            business.currencyCode!
                          )}
                          {numPointsToRedeem} of your available{' '}
                          {currencyCodeToSymbol(
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            business.currencyCode!
                          )}
                          {pluralize(possiblePointsToRedeem, 'Reward Dollar')}
                          <Anchor
                            className="ConsumerCheckout-loyaltyProgram--actionLink"
                            onClick={onEditRedemption}
                          >
                            Edit
                          </Anchor>
                        </>
                      )}
                      {numPointsToRedeem === 0 && (
                        <>
                          <div>
                            <Button variant="highContrast" size="small" onClick={onEditRedemption}>
                              Redeem reward dollars
                            </Button>
                          </div>
                        </>
                      )}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }
}
